import { useSelector } from "react-redux";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import ContactUsUpgradeBanner from "app/pages/sales-intelligence/common-components/feature-upgrade/ContactUsUpgradeBanner/ContactUsUpgradeBanner";
import { TypeOfSelectors } from "app/pages/sales-intelligence/common-components/MultiSelector/types";
import { StyledContactUsHookForExport } from "app/pages/sales-intelligence/modules/leads-search/containers/leads-actions/LeadsExportOptionsDropdown/styles";
import leadsSearchTrackingService from "app/pages/sales-intelligence/modules/leads-search/services/tracking/trackingService";
import { useExportQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useExportQuota";
import { openTrialUpgradeUrl } from "app/pages/sales-intelligence/pages/premium-features/trial";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import WebsitesDynamicsExportItemContainer from "app/pages/sales-intelligence/sub-modules/management/containers/WebsitesDynamicsExportItem/WebsitesDynamicsExportItemContainer";
import WebsitesHubSpotExportItemContainer from "app/pages/sales-intelligence/sub-modules/management/containers/WebsitesHubSpotExportItem/WebsitesHubSpotExportItemContainer";
import WebsitesSalesforceExportItemContainer from "app/pages/sales-intelligence/sub-modules/management/containers/WebsitesSalesforceExportItem/WebsitesSalesforceExportItemContainer";
import { selectIsDynamicsExportEnabled } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamicsSelectors";
import { selectIsHubspotExportEnabled } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspotSelectors";
import {
    selectIsSalesforceCompanyAsLeadExportEnabled,
    selectIsSalesforceExportEnabled,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import { demoModeKeys } from "app/pages/sales-intelligence/utils/i18n/demo-mode";
import { is } from "app/pages/sales-intelligence/utils/type-guards";
import { isTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { requestFeature } from "pro-features/components/Modals/src/HookModal/services/requestFeature";

import LeadsExcelExportItem from "../../../components/LeadsExportOptionsDropdown/LeadsExcelExportItem/LeadsExcelExportItem";
import LeadsExportOptionsDropdown from "../../../components/LeadsExportOptionsDropdown/LeadsExportOptionsDropdown";
import type { WebsitesExportOptionsDropdownProps } from "../../../components/LeadsExportOptionsDropdown/types";

type TooltipDisabledProps = WebsitesExportOptionsDropdownProps["tooltipDisabledProps"];

type Props = {
    isDisabled?: boolean;
    /** In demo mode, disable CRM export destinations while keeping Excel/CSV export available. */
    isExportDisabled?: boolean;
    tooltipProps?: TooltipDisabledProps;
    onOptionSelect(variant: TypeOfSelectors): void;
};

const TRIAL_BANNER_TEXT_KEYS = {
    contentHook: "si.multi_selector.export_options.hook.content",
    headerHook: "si.multi_selector.export_options.hook.header",
    buttonHook: "si.multi_selector.export_options.hook.button",
    buttonUpgrade: "si.common.upgrade_now",
};

const LeadsExportOptionsDropdownContainer = (props: Props) => {
    const { isLoading, isQuotaAllowed, data: quota } = useExportQuota();
    const isExportAllowedByClaim = salesSettingsHelper.isExcelAllowed();
    const allowCrmExport = !props.isExportDisabled;
    const isExportSalesForceEnabled = useSelector(selectIsSalesforceExportEnabled) && allowCrmExport;
    const isCompanyAsLeadExportEnabled = useSelector(selectIsSalesforceCompanyAsLeadExportEnabled) && allowCrmExport;
    const isExportHubspotEnabled = useSelector(selectIsHubspotExportEnabled) && allowCrmExport;
    const isExportDynamicsEnabled = useSelector(selectIsDynamicsExportEnabled) && allowCrmExport;

    const isShowHook = !(
        isExportAllowedByClaim ||
        isExportSalesForceEnabled ||
        isCompanyAsLeadExportEnabled ||
        isExportHubspotEnabled ||
        isExportDynamicsEnabled
    );

    const isTrialUser = isTrial(swSettings);

    const isDisabled = props.isDisabled || isLoading;

    const handleTrialUpgradeClick = () => {
        leadsSearchTrackingService.trackExportHookClick("upgrade");
        openTrialUpgradeUrl("leads-export-hook");
    };

    const handleContactUsClick = () => {
        leadsSearchTrackingService.trackExportHookClick("contact");
        void requestFeature("Exports");
    };

    function handleSelect(option: TypeOfSelectors) {
        trackingService.trackMultiSelectorOpen(option);
        props.onOptionSelect(option);
    }

    const getTooltipProps = (): Readonly<TooltipDisabledProps> => {
        switch (true) {
            case !is.undefined(props.tooltipProps):
                return props.tooltipProps;

            default:
                return undefined;
        }
    };

    const quotaNotAllowedTooltipContent = isQuotaAllowed ? undefined : translate("si.websites-export.disabled.text");
    const crmNotAllowedTooltipContent = salesSettingsHelper.isCrmIntegrationsEnabled()
        ? undefined
        : translate("si.websites-export.disabled.text");
    // A demo is a read-only preview, so CRM destinations are off. The items disable themselves off a truthy
    // `tooltipDisabledContent`, which is why the reason is expressed as tooltip copy rather than a flag.
    const crmDemoDisabledTooltipContent = allowCrmExport ? undefined : translate(demoModeKeys.actionDisabled);
    const crmTooltipDisabledContent =
        crmDemoDisabledTooltipContent || quotaNotAllowedTooltipContent || crmNotAllowedTooltipContent;

    return (
        <LeadsExportOptionsDropdown
            isDisabled={isDisabled}
            isCloseByClick={!isShowHook}
            tooltipDisabledProps={getTooltipProps()}
            onOpen={() => trackingService.trackMultiSelectorMenuOpen(quota.remaining)}
        >
            <LeadsExcelExportItem
                onClick={() => handleSelect(TypeOfSelectors.EXCEL)}
                tooltipDisabledContent={quotaNotAllowedTooltipContent}
            />
            <WebsitesSalesforceExportItemContainer
                tooltipDisabledContent={crmTooltipDisabledContent}
                onClick={() => handleSelect(TypeOfSelectors.EXPORT_SALES_FORCE)}
                isUseLockedIcon={false}
            />
            <WebsitesHubSpotExportItemContainer
                tooltipDisabledContent={crmTooltipDisabledContent}
                onClick={() => handleSelect(TypeOfSelectors.EXPORT_HUBSPOT)}
                isUseLockedIcon={false}
            />
            <WebsitesDynamicsExportItemContainer
                tooltipDisabledContent={crmTooltipDisabledContent}
                onClick={() => handleSelect(TypeOfSelectors.EXPORT_DYNAMICS)}
                isUseLockedIcon={false}
            />

            <>
                {isShowHook ? (
                    <StyledContactUsHookForExport>
                        <ContactUsUpgradeBanner
                            titleKey={TRIAL_BANNER_TEXT_KEYS.headerHook}
                            subtitleKey={TRIAL_BANNER_TEXT_KEYS.contentHook}
                            buttonTextKey={
                                isTrialUser ? TRIAL_BANNER_TEXT_KEYS.buttonUpgrade : TRIAL_BANNER_TEXT_KEYS.buttonHook
                            }
                            withSuccessView={!isTrialUser}
                            onSubmit={isTrialUser ? handleTrialUpgradeClick : handleContactUsClick}
                        />
                    </StyledContactUsHookForExport>
                ) : null}
            </>
        </LeadsExportOptionsDropdown>
    );
};

export default LeadsExportOptionsDropdownContainer;
